<template>
  <HeaderBar />
  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'vehicleTypes' }">{{ t('backToVehicleTypesList') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <div v-if="!isLoading">
      <VehicleTypeStats
        v-if="vehicleType"
        :vehicle-type="vehicleType"
        :hide-edit-button="!canEdit"
      />

      <Divider />

      <div class="flex justify-between">
        <h2>{{ t('vehiclesOfVehicleType') }}</h2>
        <CVButton
          v-if="canEdit"
          :disabled="!vehicleType"
          @click="
            $router.push({
              name: 'addNewCar',
              params: { vehicleTypeId: vehicleType?.id },
            })
          "
        >
          {{ t('addVehicle') }}
        </CVButton>
      </div>

      <div v-if="vehicleType" class="mt-4 flex flex-col space-y-2">
        <div v-for="(car, index) in visibleCars" :key="car.id">
          <CarCard :car="car" :vehicle-type="vehicleType" @click="chosenCar = index" />
        </div>
      </div>
    </div>
    <div v-else class="mt-10 flex items-center justify-center">
      <Spinner />
    </div>
  </section>
</template>

<script setup lang="ts">
import { useUiStore } from '@/stores/ui.store';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useVehicleType } from '@/queries/use-vehicle-types';
import { computed, ref, watchEffect } from 'vue';
import VehicleTypeStats from './VehicleTypeStats.vue';
import Divider from '@/components/Divider.vue';
import CarCard from '../vehicle-types-list/CarCard.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { useCarsWithQuery } from '@/queries/use-cars.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import { ability } from '@/main.ts';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const { data: vehicleType, isLoading: isLoading } = useVehicleType(route.params.id as string);
const { data: carsData } = useCarsWithQuery({
  vehicleTypeIds: [route.params.id as string],
  limit: 10000,
});
const cars = useFlattenPaginatedData(carsData);

const canEdit = computed(() => ability.can('ros', 'VehiclePool'));

const visibleCars = computed(() =>
  canEdit.value ? cars.value : cars.value.filter((car) => !car.isGhostCar),
);

watchEffect(() => {
  if (vehicleType.value) {
    const name = vehicleType.value.make.name;
    const model = vehicleType.value.model;
    uiStore.setHeaderTitle(t('vehicleTypes'), name, model);
  }
});

const chosenCar = ref(0);
</script>

<i18n lang="json">
{
  "en": {
    "vehicleTypes": "Vehicle Types",
    "newVehicleType": "New Vehicle Type",
    "addVehicle": "+ add vehicle",
    "vehiclesOfVehicleType": "Vehicles of the Vehicle Type",
    "backToVehicleTypesList": "Back to Vehicle Types List"
  },
  "de": {
    "vehicleTypes": "Vehicle Types",
    "newVehicleType": "Neue Fahrzeugklasse",
    "addVehicle": "+ Fahrzeug hinzufügen",
    "vehiclesOfVehicleType": "Fahrzeuge des Vehicle Type",
    "backToVehicleTypesList": "Zurück zur Vehicle Types Liste"
  },
  "es": {
    "vehicleTypes": "Tipos de vehículo",
    "newVehicleType": "Nuevo tipo de vehículo",
    "addVehicle": "+ Añadir vehículo",
    "vehiclesOfVehicleType": "Vehículos de este tipo",
    "backToVehicleTypesList": "Volver a la lista de tipos de vehículo"
  }
}
</i18n>
